/**
 * F-126/F-139 BGM coverage — the ONE rule that decides whether a board's
 * music is done: every MOOD run of the script has at least `BGM_MIN_STEMS`
 * music stems, and they sit on the run's TOP cell.
 *
 * Pure and input-shaped rather than doc-shaped, so BOTH sides run the same
 * code: the client feeds it the live grid (to draw the slots and name the
 * gaps), and the server feeds it the loaded board doc (to derive the stems
 * tag every dashboard surface shows). A rule implemented twice is a rule that
 * will eventually disagree with itself.
 *
 * WHAT A RUN IS. Moods are not entities — `applySections` writes a mood LABEL
 * into every row's "Mood" cell (docs: features/sections). A run is therefore a
 * maximal CONTIGUOUS RUN of equal, non-empty labels in sheet order: the
 * stretch between two mood changes, exactly the unit the preview's mood
 * markers bound. One label appearing in two places is two runs, and —
 * deliberately — two stem slots: the same music may well return, but that is
 * the designer's call to make twice, not an assumption this module makes for
 * them. (Boards divided before F-139 have a "Section" column instead, and
 * their runs are section runs — `bgmColumns` picks the column; the rule is
 * the same.)
 *
 * WHY TWO STEMS. Music is reviewed by listening to candidates against each
 * other; a slot with one stem has nothing to choose between. So a run is
 * covered only once its top cell carries `BGM_MIN_STEMS` or more.
 *
 * Rows with an EMPTY label belong to no run. They are skipped entirely: they
 * neither open a run nor break one open, so a stray unlabeled row in the
 * middle of a run does not split it into two slots.
 *
 * A ROW MAY ALSO CARRY A GROUP (F-141: the `Short` column on a Shorts board).
 * A run never crosses a group boundary: two adjacent shorts both in "Hype"
 * are two runs with two slots, never one — each short has its own music.
 * Rows with no group are the pre-F-141 case and group nothing.
 */
import { BGM_MIN_STEMS } from "./doc/schema.js";
/**
 * Group `rows` (already in sheet order) into runs and report which ones carry
 * enough stems.
 */
export function computeBgmCoverage(rows) {
    const runs = [];
    let current = null;
    for (const row of rows) {
        const title = row.label.trim();
        if (title === "")
            continue; // unlabeled rows join no run and break none
        const group = row.group?.trim() ?? "";
        if (current === null || current.title !== title || (current.group ?? "") !== group) {
            current = {
                title,
                ...(group !== "" ? { group } : {}),
                topRowId: row.rowId,
                rowIds: [row.rowId],
                stems: row.stems,
                covered: row.stems >= BGM_MIN_STEMS,
                misplacedStem: false,
            };
            runs.push(current);
            continue;
        }
        current.rowIds.push(row.rowId);
        if (row.stems > 0)
            current.misplacedStem = true;
    }
    // A run whose top cell is covered does not "also" have a misplaced stem in
    // any sense worth reporting — extra stems below are simply extra.
    for (const run of runs) {
        if (run.covered)
            run.misplacedStem = false;
    }
    const covered = runs.filter((run) => run.covered).length;
    return { runs, covered, total: runs.length, ready: runs.length > 0 && covered === runs.length };
}
/** The uncovered runs, in script order — the "still missing" list the UI names. */
export function bgmCoverageGaps(coverage) {
    return coverage.runs
        .filter((run) => !run.covered)
        .map((run) => ({ title: run.title, topRowId: run.topRowId, stems: run.stems }));
}
//# sourceMappingURL=bgm-coverage.js.map